import React, { useState } from "react";
import { DashboardLayout } from "src/components/dashboard";
import { useNavigate, useParams } from "react-router-dom";
import PartnerBasicInfoForm from "src/components/partners/PartnerBasicInfoForm";
import PartnerMediaUpload from "src/components/partners/PartnerMediaUpload";
import PartnerFormHeader from "src/components/partners/PartnerFormHeader";
import PartnerFormNavigation from "src/components/partners/PartnerFormNavigation";
import {
  AddPartnerPayload,
  useAddPartnerMutation,
  useGetCountryDetailsQuery,
  useUploadPartnerLogoMutation,
  useAddOperatingHoursPartnerMutation,
} from "src/store/services/partnerApi";
import { toast, ToastContainer } from "react-toastify";

// Extended form data type to include all UI fields
type PartnerFormData = {
  businessName: string;
  email: string;
  phoneNumber: string;
  phoneCountryCode: string;
  yearsOfOperation: string;
  physicalAddress: string;
  city: string;
  stateProvince: string;
  zipPostalCode: string;
  country: string;
  businessHoursOpen: string;
  businessHoursClose: string;
  isRegistered: string;
  rcNumber: string;
  isOwner: string;
  designation: string;
  businessBio: string;
  logo: File | null;
  gallery: File[];
  businessAccountManagerFirstName: string;
  businessAccountManagerLastName: string;
  businessAccountManagerPhoneNumber: string;
  businessAccountManagerPhoneCountryCode: string;
  businessAccountManagerGender: string;
  businessAccountManagerDesignation: string;
  accountManagerSourceBy: string;
  accountManagerPartnershipManager: string;
  latitude: string;
  longitude: string;
  businessAccountOfficerPhoneNumber: string;
  can_deliver: string;
  businessAccountOfficerPhoneCountryCode: string;
};

const AddNewPartners = () => {
  const navigate = useNavigate();
  const { categoryName } = useParams<{ categoryName: string }>();
  const [addPartner, { isLoading }] = useAddPartnerMutation();
  const [addPartnerHours, { isLoading: isdloading }] = useAddOperatingHoursPartnerMutation();
  const { data: countryDetails } = useGetCountryDetailsQuery();
  const [uploadPartnerLogo, { isLoading: isUploading }] =
    useUploadPartnerLogoMutation();
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<PartnerFormData>({
    businessName: "",
    email: "",
    phoneNumber: "",
    phoneCountryCode: "",
    yearsOfOperation: "",
    physicalAddress: "",
    city: "",
    stateProvince: "", 
    zipPostalCode: "",
    country: "",
    businessHoursOpen: "",
    businessHoursClose: "",
    isRegistered: "",
    rcNumber: "",
    isOwner: "",
    designation: "",
    businessBio: "",
    logo: null,
    gallery: [],
    businessAccountManagerFirstName: "",
    businessAccountManagerLastName: "",
    businessAccountManagerPhoneNumber: "",
    businessAccountManagerPhoneCountryCode: "",
    businessAccountManagerGender: "",
    businessAccountManagerDesignation: "",
    accountManagerSourceBy: "",
    accountManagerPartnershipManager: "",
    latitude: "",
    longitude: "",
    businessAccountOfficerPhoneNumber: "",
    can_deliver: "",
    businessAccountOfficerPhoneCountryCode: "",
  });

  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ] as const;

  type Day = (typeof days)[number];

  type OperationalHours = {
    [key in Day]: {
      is_open: boolean;
      open: string;
      close: string;
    };
  };

  const [opeFormData, setOpeFormData] = useState<{
    operational_hours: OperationalHours;
  }>({
    operational_hours: {
      monday: { is_open: true, open: "08:00", close: "18:00" },
      tuesday: { is_open: true, open: "08:00", close: "18:00" },
      wednesday: { is_open: true, open: "08:00", close: "18:00" },
      thursday: { is_open: true, open: "08:00", close: "18:00" },
      friday: { is_open: true, open: "08:00", close: "16:00" },
      saturday: { is_open: true, open: "10:00", close: "14:00" },
      sunday: { is_open: false, open: "", close: "" },
    },
  });

  const handleOperationalHoursChange = (
    day: Day,
    field: "is_open" | "open" | "close",
    value: string | boolean,
  ) => {
    setOpeFormData((prev) => ({
      ...prev,
      operational_hours: {
        ...prev.operational_hours,
        [day]: {
          ...prev.operational_hours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // console.log(countryDetails);

  const handleLogoUpload = (file: File) => {
    setFormData((prev) => ({ ...prev, logo: file }));
  };

  // Handle place selection to update location coordinates
  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    if (place.geometry?.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setFormData((prev) => ({
        ...prev,
        latitude: lat.toString(),
        longitude: lng.toString(),
      }));
    }
  };

  // Transform form data to API payload (without logo - logo is uploaded separately)
  const transformToApiPayload = (): AddPartnerPayload => {
    const accountManagerFirstName =
      formData.businessAccountManagerFirstName.trim();
    const accountManagerLastName =
      formData.businessAccountManagerLastName.trim();

    // Combine country code and business phone number
    const businessPhoneNumber = formData.phoneCountryCode
      ? `${formData.phoneCountryCode}${formData.phoneNumber.replace(
          /\s+/g,
          "",
        )}`
      : formData.phoneNumber.trim();

    // Combine country code and account manager phone number
    const accountManagerPhoneNumber =
      formData.businessAccountManagerPhoneCountryCode
        ? `${
            formData.businessAccountManagerPhoneCountryCode
          }${formData.businessAccountManagerPhoneNumber.replace(/\s+/g, "")}`
        : formData.businessAccountManagerPhoneNumber;

    // Combine country code and account manager phone number
    const accountOfficerPhoneNumber =
      formData.businessAccountOfficerPhoneCountryCode
        ? `${
            formData.businessAccountOfficerPhoneCountryCode
          }${formData.businessAccountOfficerPhoneNumber.replace(/\s+/g, "")}`
        : formData.businessAccountOfficerPhoneNumber;

    const countryCode = countryDetails?.payload?.data?.find(
      (country) => country.name === formData.country,
    )?.code;

    const payload: AddPartnerPayload = {
      email: formData.email.trim(),
      phone: businessPhoneNumber,
      partner_address: formData.physicalAddress.trim(),
      zip_code: formData.zipPostalCode.trim(),
      years_of_operation: parseInt(formData.yearsOfOperation) || 0,
      business_hours_open: formData.businessHoursOpen || "08:00",
      business_hours_close: formData.businessHoursClose || "17:00",
      is_registered:
        formData.isRegistered === "yes" || formData.isRegistered === "true",
      can_deliver:
        formData.can_deliver === "yes" || formData.can_deliver === "true",
      is_owner: formData.isOwner === "yes" || formData.isOwner === "true",
      rc_number: formData.rcNumber.trim(),
      city: formData.city.trim(),
      state: formData.stateProvince.trim(),
      country: countryCode || "",
      contact_person: accountManagerPhoneNumber || businessPhoneNumber,
      category_id: categoryName || "",
      business_name: formData.businessName.trim(),
      bio: formData.businessBio.trim(),
      // Account manager information
      first_name: accountManagerFirstName,
      last_name: accountManagerLastName,
      designation:
        formData.businessAccountManagerDesignation?.trim() ||
        formData.designation?.trim() ||
        "",
      gender: formData.businessAccountManagerGender || "male",
      account_manager_phone: accountOfficerPhoneNumber,
      source_by: formData.accountManagerSourceBy.trim(),
      partnership_manager: formData.accountManagerPartnershipManager.trim(),
      ...(formData.latitude &&
        formData.longitude && {
          latitude: formData.latitude,
          longitude: formData.longitude,
          // location: `${formData.latitude},${formData.longitude}`,
        }),
    };

    return payload;
  };

  const handleNext = () => {
    handleSubmit();
  };

  const handleSubmit = async () => {
    setError(null);

    // Validate required fields
    if (!formData.businessName.trim()) {
      setError("Business name is required");
      return;
    }

    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }

    if (!formData.logo) {
      setError("Business logo is required");
      return;
    }
    if (!formData.phoneNumber.trim()) {
      setError("Phone number is required");
      return;
    }
    if (!formData.phoneCountryCode) {
      setError("Please select a country to get the dial code");
      return;
    }
    if (!formData.country.trim()) {
      setError("Country is required");
      return;
    }
    if (!categoryName) {
      setError("Category is required");
      return;
    }
    // Validate bio character limit
    if (formData.businessBio.length > 420) {
      setError("Business bio cannot exceed 420 characters");
      return;
    }

    try {
          // await addPartnerHours({
          //   id: partnerId as string,
          //   data: opeFormData,
          // }).unwrap();
    
          // toast.success(
          //   `${selectedFiles.length} image${
          //     selectedFiles.length > 1 ? "s" : ""
          //   } uploaded successfully`,
          // );
    
          // Call the optional callback if provided
          // if (onAddImage) {
          //   onAddImage();
          // }
    
          // Close modal and reset state
          // handleCloseModal();
        } catch (error: any) {
          toast.error(
            error?.data?.message || "Failed to upload images. Please try again.",
          );
        }
        setError(null);

    try {
      const apiPayload = transformToApiPayload();

      // Step 1: Add partner first
      const addPartnerResponse = await addPartner(apiPayload).unwrap();

      // Extract partner ID from response
      // The response structure may vary, try common patterns
      const partnerId =
        addPartnerResponse?.payload?.id ||
        addPartnerResponse?.payload?._id ||
        addPartnerResponse?.payload?.partner_id ||
        (addPartnerResponse?.payload as any)?.partner?.id ||
        (addPartnerResponse?.payload as any)?.partner?._id;

      if (!partnerId) {
        console.error("Partner ID not found in response:", addPartnerResponse);
        setError(
          "Partner created successfully but could not retrieve partner ID for logo upload.",
        );
        // Still navigate back even if logo upload fails
        setTimeout(() => {
          navigate(`/partners/${categoryName || ""}`);
        }, 2000);
        return;
      }

      // Step 2: Upload logo if provided
      if (formData.logo) {
        try {
          await uploadPartnerLogo({
            id: partnerId,
            image: formData.logo,
          }).unwrap();
          console.log("Logo uploaded successfully");
        } catch (logoError: any) {
          console.error("Logo upload failed:", logoError);
          // Don't fail the whole operation if logo upload fails
          setError(
            logoError?.data?.message ||
              "Partner created but logo upload failed. You can upload it later.",
          );
          // Still navigate back
          setTimeout(() => {
            navigate(`/partners/${categoryName || ""}`);
          }, 3000);
          return;
        }
      }

      // Success - navigate back to category partners
      navigate(`/partners/${categoryName || ""}`);
    } catch (err: any) {
      console.error("Failed to add partner:", err);
      const errorMessage =
        err?.data?.message ||
        err?.message ||
        err?.data?.error?.message ||
        "Failed to add partner. Please try again.";
      console.log("Error Message:", errorMessage);
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleBack = () => {
    navigate(`/partners/${categoryName || ""}`);
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        <PartnerFormHeader
          title={"Business Information"}
          description={"Enter the business details"}
        />

        <PartnerBasicInfoForm
          showCover={false}
          handleFileSelect={() => {
            return false;
          }}
          formData={formData}
          opeFormData={opeFormData}
          onInputChange={handleInputChange}
          onLogoUpload={handleLogoUpload}
          logo={formData.logo}
          countryDetails={countryDetails?.payload?.data || []}
          onPlaceSelect={handlePlaceSelect}
          handleOperationalHours={handleOperationalHoursChange}
        />

        <PartnerFormNavigation
          onBack={handleBack}
          onNext={handleNext}
          isLoading={isLoading || isUploading}
          disabled={isLoading || isUploading}
        />
      </div>
      <ToastContainer />
    </DashboardLayout>
  );
};

export default AddNewPartners;
